import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronLeft, FileCheck, AlertCircle, Upload, Loader2, Sparkles, CheckCircle2, X, CreditCard, Trash2, TrendingUp, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '@/services/apiService';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

const DocumentRequirements = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visaType } = location.state || {};
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { trackEvent } = useAnalytics();
  
  const { 
    uploadQueue, 
    isInQueue, 
    removeFromQueue, 
    clearQueue,
    queuePricing,
    setCurrentVisaType 
  } = useApp();

  useEffect(() => {
    if (!visaType) {
      navigate('/select-country');
      return;
    }
    setCurrentVisaType(visaType);
    loadDocuments();
    
    // Track page view
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_REQUIREMENTS_VIEW, { visa_type: visaType });
  }, [visaType, navigate, setCurrentVisaType, trackEvent]);

  // Check for stale queue entries and warn user
  useEffect(() => {
    const checkQueueValidity = async () => {
      if (uploadQueue.length === 0) return;
      
      // Check if the first item in queue is still valid on backend
      try {
        const pendingIds = uploadQueue.map(d => d.pendingId);
        await apiService.refreshPendingDocuments(pendingIds);
      } catch (error) {
        // If refresh fails, the pending uploads have likely expired
        console.warn('Queue items may have expired, clearing queue');
        clearQueue();
      }
    };
    
    checkQueueValidity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const loadDocuments = async () => {
    try {
      const docs = await apiService.filterDocuments(null, visaType);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = useCallback((doc) => {
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_STARTED, { 
      document_type: doc.name,
      visa_type: visaType 
    });
    
    navigate('/upload', {
      state: {
        selectedDocument: {
          id: doc.id,
          name: doc.name,
          category: doc.category,
          requires_apostille: doc.requires_apostille,
          requires_translation: doc.requires_translation,
          validation_notes: doc.validation_notes,
          typical_source: doc.typical_source
        },
        visaType
      }
    });
  }, [navigate, visaType, trackEvent]);

  const handleProceedToPayment = useCallback(() => {
    if (uploadQueue.length === 0) return;
    
    trackEvent(ANALYTICS_EVENTS.PAYMENT_PAGE_VIEW, {
      document_count: uploadQueue.length,
      total_amount: queuePricing.total,
      visa_type: visaType
    });
    
    // Store pending IDs in localStorage for payment flow
    const pendingIds = uploadQueue.map(d => d.pendingId);
    localStorage.setItem('pending_document_ids', JSON.stringify(pendingIds));
    
    navigate('/payment-checkout', {
      state: {
        visaType,
        documentCount: uploadQueue.length,
        queuePricing
      }
    });
  }, [uploadQueue, navigate, visaType, queuePricing, trackEvent]);

  const getCategoryColor = useCallback((category) => {
    const colorMap = {
      'Identity': 'bg-trust/10 text-trust border-trust/20',
      'Legal': 'bg-verify/10 text-verify border-verify/20',
      'Employment': 'bg-warning/10 text-warning border-warning/20',
      'Education': 'bg-violet-100 text-violet-700 border-violet-200',
      'Financial': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'Family': 'bg-rose-100 text-rose-700 border-rose-200',
      'Health': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'Insurance': 'bg-blue-100 text-blue-700 border-blue-200',
      'Proof of Residence': 'bg-purple-100 text-purple-700 border-purple-200',
    };
    return colorMap[category] || 'bg-slate-100 text-slate-700 border-slate-200';
  }, []);

  // Memoize uploaded document names for quick lookup
  const uploadedDocNames = useMemo(() => {
    return new Set(uploadQueue.map(d => d.documentType));
  }, [uploadQueue]);

  // Calculate document completeness
  const completenessStats = useMemo(() => {
    const requiredDocs = documents.filter(d => d.priority?.includes('P1'));
    const optionalDocs = documents.filter(d => !d.priority?.includes('P1'));
    
    const uploadedRequired = requiredDocs.filter(d => 
      uploadQueue.some(q => q.documentType === d.name || q.selectedDocument?.name === d.name)
    );
    const uploadedOptional = optionalDocs.filter(d => 
      uploadQueue.some(q => q.documentType === d.name || q.selectedDocument?.name === d.name)
    );
    
    const requiredCount = requiredDocs.length;
    const uploadedRequiredCount = uploadedRequired.length;
    const percentage = requiredCount > 0 ? Math.round((uploadedRequiredCount / requiredCount) * 100) : 0;
    
    const missingRequired = requiredDocs.filter(d => 
      !uploadQueue.some(q => q.documentType === d.name || q.selectedDocument?.name === d.name)
    );
    
    return {
      requiredTotal: requiredCount,
      requiredUploaded: uploadedRequiredCount,
      optionalTotal: optionalDocs.length,
      optionalUploaded: uploadedOptional.length,
      percentage,
      missingRequired,
      isComplete: uploadedRequiredCount >= requiredCount
    };
  }, [documents, uploadQueue]);

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/select-country')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-24"></div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2">
              Required Documents
            </h1>
            <p className="font-public text-lg text-slate-600">
              {visaType}
            </p>
            <p className="font-public text-sm text-slate-500 mt-2">
              Click on a document to upload. Your files are processed temporarily and deleted after analysis.
            </p>
          </div>

          {/* Document Completeness Checker */}
          {!loading && documents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${completenessStats.percentage >= 100 ? 'bg-verify/10' : 'bg-trust/10'}`}>
                      <TrendingUp className={`w-5 h-5 ${completenessStats.percentage >= 100 ? 'text-verify' : 'text-trust'}`} />
                    </div>
                    <div>
                      <h3 className="font-outfit font-bold text-lg text-slate-900">
                        Document Completeness
                      </h3>
                      <p className="text-sm text-slate-500">
                        {completenessStats.isComplete 
                          ? 'All required documents uploaded!' 
                          : `${completenessStats.requiredTotal - completenessStats.requiredUploaded} required documents remaining`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-2xl font-outfit font-bold ${
                      completenessStats.percentage >= 100 ? 'text-verify' : 
                      completenessStats.percentage >= 50 ? 'text-trust' : 'text-warning'
                    }`}>
                      {completenessStats.percentage}%
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${completenessStats.percentage}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`h-full rounded-full ${
                      completenessStats.percentage >= 100 ? 'bg-verify' : 
                      completenessStats.percentage >= 50 ? 'bg-trust' : 'bg-warning'
                    }`}
                  />
                </div>

                {/* Stats Row */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-verify" />
                    <span className="text-slate-600">
                      <span className="font-semibold text-slate-900">{completenessStats.requiredUploaded}</span>
                      /{completenessStats.requiredTotal} required
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      <span className="font-semibold text-slate-900">{completenessStats.optionalUploaded}</span>
                      /{completenessStats.optionalTotal} optional
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-trust" />
                    <span className="text-slate-600">
                      <span className="font-semibold text-slate-900">{uploadQueue.length}</span> in queue
                    </span>
                  </div>
                </div>

                {/* Missing Required Documents Warning */}
                {completenessStats.missingRequired.length > 0 && completenessStats.missingRequired.length <= 3 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-slate-700">Still needed: </span>
                        <span className="text-slate-600">
                          {completenessStats.missingRequired.slice(0, 3).map(d => d.name).join(', ')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Upload Queue Panel - Fixed at top when has items */}
          <AnimatePresence>
            {uploadQueue.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-8"
              >
                <div className="bg-verify/5 border-2 border-verify rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-verify/10 p-2 rounded-lg">
                        <FileCheck className="w-6 h-6 text-verify" />
                      </div>
                      <div>
                        <h3 className="font-outfit font-bold text-lg text-slate-900">
                          {uploadQueue.length} Document{uploadQueue.length > 1 ? 's' : ''} Ready for Analysis
                        </h3>
                        <p className="font-public text-sm text-slate-600">
                          Upload more documents or proceed to payment
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearQueue}
                      className="text-slate-400 hover:text-destructive transition-colors p-2"
                      data-testid="clear-queue-button"
                      title="Clear all uploads"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Queue Items */}
                  <div className="space-y-2 mb-4">
                    {uploadQueue.map((doc, idx) => (
                      <motion.div
                        key={doc.pendingId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-center justify-between bg-white/80 rounded-md px-4 py-2 border border-slate-200"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-verify" />
                          <span className="font-public text-sm text-slate-700">{doc.documentType}</span>
                          <span className="text-xs text-slate-400">({doc.filename})</span>
                        </div>
                        <button
                          onClick={() => removeFromQueue(doc.pendingId)}
                          className="text-slate-400 hover:text-destructive transition-colors p-1"
                          data-testid={`remove-queue-item-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pricing Summary */}
                  <div className="bg-white/50 rounded-md p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="font-public text-slate-600">
                        {queuePricing.documentCount} document{queuePricing.documentCount > 1 ? 's' : ''} × ${queuePricing.perDocument}
                      </span>
                      <span className="font-outfit font-bold text-lg text-slate-900">
                        ${queuePricing.total}
                      </span>
                    </div>
                    {queuePricing.savings > 0 && (
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-verify font-medium">
                          {queuePricing.discount}% bundle discount applied!
                        </span>
                        <span className="text-sm text-verify font-medium">
                          Saving ${queuePricing.savings}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-trust animate-spin" />
            </div>
          )}

          {/* Documents List */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.map((doc) => {
                const isUploaded = uploadedDocNames.has(doc.name) || 
                  uploadQueue.some(q => q.selectedDocument?.name === doc.name);
                
                return (
                  <motion.div
                    key={doc.id}
                    whileHover={{ scale: 1.01 }}
                    className={`group cursor-pointer rounded-lg border-2 transition-all bg-white shadow-sm hover:shadow-md ${
                      isUploaded 
                        ? 'border-verify bg-verify/5' 
                        : 'border-slate-200 hover:border-trust'
                    }`}
                    onClick={() => handleUploadDocument(doc)}
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`font-outfit font-bold text-lg transition-colors ${
                              isUploaded ? 'text-verify' : 'text-slate-900 group-hover:text-trust'
                            }`}>
                              {doc.name}
                            </h3>
                            {isUploaded && (
                              <CheckCircle2 className="w-5 h-5 text-verify flex-shrink-0" />
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            {doc.category && (
                              <span className={`px-2 py-0.5 rounded-md border text-xs font-medium ${getCategoryColor(doc.category)}`}>
                                {doc.category}
                              </span>
                            )}
                            {doc.priority?.includes('P1') && (
                              <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs font-mono uppercase rounded">
                                Required
                              </span>
                            )}
                            {isUploaded && (
                              <span className="px-2 py-0.5 bg-verify/10 text-verify text-xs font-medium rounded">
                                Uploaded
                              </span>
                            )}
                          </div>
                          
                          {/* Requirements badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {doc.requires_apostille && (
                              <span className="px-2 py-1 bg-warning/10 text-warning border border-warning/20 rounded text-xs">
                                Apostille Required
                              </span>
                            )}
                            {doc.requires_translation && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 border border-blue-200 rounded text-xs">
                                Translation Required
                              </span>
                            )}
                          </div>
                          
                          {doc.typical_source && (
                            <p className="font-public text-sm text-slate-500 truncate">
                              Source: {doc.typical_source}
                            </p>
                          )}
                        </div>
                        
                        {/* Upload/Replace Icon */}
                        <div className={`flex-shrink-0 p-3 rounded-lg transition-all ${
                          isUploaded 
                            ? 'bg-verify/10 group-hover:bg-warning group-hover:text-white'
                            : 'bg-trust/10 group-hover:bg-trust group-hover:text-white'
                        }`}>
                          <Upload className={`w-5 h-5 transition-colors ${
                            isUploaded 
                              ? 'text-verify group-hover:text-white'
                              : 'text-trust group-hover:text-white'
                          }`} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Upload Other Document Card */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                className="group cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-trust transition-all bg-white/50 shadow-sm hover:shadow-md"
                onClick={() => navigate('/upload', { state: { visaType } })}
                data-testid="upload-other-document"
              >
                <div className="p-5 flex items-center justify-center min-h-[120px]">
                  <div className="text-center">
                    <div className="bg-trust/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-trust transition-colors">
                      <Sparkles className="w-6 h-6 text-trust group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-outfit font-bold text-lg text-slate-700 group-hover:text-trust transition-colors">
                      Upload Other Document
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Analyze any document not listed
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Floating Payment Button */}
      <AnimatePresence>
        {uploadQueue.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 p-4 z-50"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-verify/10 p-2 rounded-lg">
                  <CreditCard className="w-5 h-5 text-verify" />
                </div>
                <div>
                  <p className="font-public font-semibold text-slate-900">
                    {uploadQueue.length} document{uploadQueue.length > 1 ? 's' : ''} ready
                  </p>
                  <p className="text-sm text-slate-500">
                    Total: <span className="font-semibold text-slate-900">${queuePricing.total}</span>
                    {queuePricing.savings > 0 && (
                      <span className="text-verify ml-2">(Save ${queuePricing.savings})</span>
                    )}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleProceedToPayment}
                className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 active:scale-95 transition-all flex items-center gap-2 shadow-md hover:shadow-lg"
                data-testid="proceed-to-payment-button"
              >
                <Sparkles className="w-5 h-5" />
                Proceed to Payment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentRequirements;
